import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Download, Trash2, LogOut } from 'lucide-react';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;
const TZ = 'Asia/Dubai';

const fmtDateTime = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      timeZone: TZ,
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
    }).replace(',', '');
  } catch { return iso; }
};
const fmtHM = (hours) => {
  if (hours == null || isNaN(hours)) return '0:00';
  const totalMin = Math.round(Number(hours) * 60);
  const h = Math.floor(Math.max(0, totalMin) / 60);
  const m = Math.max(0, totalMin) % 60;
  return `${h}:${String(m).padStart(2, '0')}`;
};

const ReportsTab = ({ onClockOut }) => {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const today = new Date().toISOString().slice(0, 10);
  const [reportForm, setReportForm] = useState({ scope: 'daily', start_date: today, end_date: today });
  const [report, setReport] = useState(null);
  const [weeklyCol, setWeeklyCol] = useState(true); // show Weekly Hours column
  const [expandedReportRows, setExpandedReportRows] = useState({});
  const [selectedSessions, setSelectedSessions] = useState({});
  const [selectedSummary, setSelectedSummary] = useState({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const runReport = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/clock/report`, { headers, params: reportForm });
      setReport(r.data);
    } catch (e) {
      toast({ title: 'Report failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportForm]);

  useEffect(() => { runReport(); /* on mount */ /* eslint-disable-next-line */ }, []);

  const exportReport = (fmt) => {
    const params = { ...reportForm };
    if (weeklyCol) params.weekly_compliance = 'true';
    const qs = new URLSearchParams(params).toString();
    const url = `${API}/api/clock/report/export.${fmt}?${qs}`;
    axios.get(url, { headers, responseType: 'blob' })
      .then(r => {
        const blob = new Blob([r.data]);
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `clock_report_${reportForm.start_date}_to_${reportForm.end_date}.${fmt}`;
        document.body.appendChild(a); a.click(); a.remove();
      })
      .catch(() => toast({ title: 'Export failed', variant: 'destructive' }));
  };

  const saveDayNote = async (employeeId, dateStr, note) => {
    if (!employeeId || !dateStr) return;
    try {
      await axios.put(`${API}/api/clock/admin/day-notes`,
        { employee_id: employeeId, date: dateStr, note: note || '' },
        { headers }
      );
      setReport(prev => {
        if (!prev) return prev;
        return {
          ...prev,
          rows: prev.rows.map(r => r.employee_id === employeeId ? {
            ...r,
            sessions: (r.sessions || []).map(s => s.date === dateStr ? { ...s, note: note || '' } : s),
          } : r),
        };
      });
    } catch {
      toast({ title: 'Failed to save note', variant: 'destructive' });
    }
  };

  const refreshAfterDelete = () => { runReport(); setSelectedSessions({}); setSelectedSummary({}); };

  const deleteSession = async (s) => {
    const ids = [s.in_event_id, s.out_event_id].filter(Boolean);
    if (ids.length === 0) return;
    if (!window.confirm(`Delete this clock-in / clock-out pair?\nThis removes ${ids.length} event(s).`)) return;
    try {
      await axios.post(`${API}/api/clock/events/bulk-delete`, { event_ids: ids }, { headers });
      toast({ title: 'Session deleted' });
      refreshAfterDelete();
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const deleteEmployeeRow = async (r) => {
    const ids = [];
    (r.sessions || []).forEach(s => {
      if (s.in_event_id) ids.push(s.in_event_id);
      if (s.out_event_id) ids.push(s.out_event_id);
    });
    if (r.open_event_id) ids.push(r.open_event_id);
    if (ids.length === 0) { toast({ title: 'Nothing to delete for this row' }); return; }
    if (!window.confirm(`Delete ALL ${ids.length} clock event(s) for ${r.employee_name} in the selected range?`)) return;
    try {
      await axios.post(`${API}/api/clock/events/bulk-delete`, { event_ids: ids }, { headers });
      toast({ title: `Deleted ${ids.length} event(s) for ${r.employee_name}` });
      refreshAfterDelete();
    } catch { toast({ title: 'Delete failed', variant: 'destructive' }); }
  };

  const bulkDeleteSelected = async () => {
    if (!report) return;
    const ids = new Set();
    report.rows.forEach(r => {
      if (selectedSummary[r.employee_id]) {
        (r.sessions || []).forEach(s => {
          if (s.in_event_id) ids.add(s.in_event_id);
          if (s.out_event_id) ids.add(s.out_event_id);
        });
        if (r.open_event_id) ids.add(r.open_event_id);
      }
    });
    report.rows.forEach(r => {
      (r.sessions || []).forEach((s, i) => {
        const key = `${r.employee_id}|${i}`;
        if (selectedSessions[key]) {
          if (s.in_event_id) ids.add(s.in_event_id);
          if (s.out_event_id) ids.add(s.out_event_id);
        }
      });
    });
    const idList = Array.from(ids);
    if (idList.length === 0) { toast({ title: 'Select rows first', variant: 'destructive' }); return; }
    if (!window.confirm(`Delete ${idList.length} clock event(s) from selected rows?`)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(`${API}/api/clock/events/bulk-delete`, { event_ids: idList }, { headers });
      toast({ title: `Deleted ${res.data.deleted} event(s)` });
      refreshAfterDelete();
    } catch { toast({ title: 'Bulk delete failed', variant: 'destructive' }); }
    finally { setBulkBusy(false); }
  };

  const totalSelected = Object.values(selectedSummary).filter(Boolean).length + Object.values(selectedSessions).filter(Boolean).length;

  return (
    <div className="space-y-3">
      <Card><CardContent className="p-3 flex flex-wrap gap-2 items-end">
        <div><Label className="text-xs">Scope</Label>
          <select
            value={reportForm.scope}
            onChange={e => {
              const scope = e.target.value;
              setReportForm(f => {
                const anchor = new Date(f.start_date || today);
                let { start_date, end_date } = f;
                if (scope === 'weekly') {
                  const d = new Date(anchor);
                  const dow = (d.getDay() + 6) % 7;
                  d.setDate(d.getDate() - dow);
                  start_date = d.toISOString().slice(0, 10);
                  d.setDate(d.getDate() + 6);
                  end_date = d.toISOString().slice(0, 10);
                } else if (scope === 'monthly') {
                  const y = anchor.getFullYear();
                  const m = anchor.getMonth();
                  start_date = new Date(y, m, 1).toISOString().slice(0, 10);
                  end_date = new Date(y, m + 1, 0).toISOString().slice(0, 10);
                }
                return { ...f, scope, start_date, end_date };
              });
            }}
            className="px-2 py-1.5 border rounded-md text-sm"
            data-testid="report-scope"
          >
            <option value="hourly">Hourly</option>
            <option value="daily">Daily</option>
            <option value="weekly">Weekly</option>
            <option value="monthly">Monthly</option>
          </select></div>
        <div><Label className="text-xs">Start</Label>
          <Input
            type="date"
            value={reportForm.start_date}
            onChange={e => {
              const start = e.target.value;
              setReportForm(f => {
                let end_date = f.end_date;
                if (f.scope === 'weekly' && start) {
                  const d = new Date(start); d.setDate(d.getDate() + 6);
                  end_date = d.toISOString().slice(0, 10);
                } else if (f.scope === 'monthly' && start) {
                  const d = new Date(start);
                  end_date = new Date(d.getFullYear(), d.getMonth() + 1, 0).toISOString().slice(0, 10);
                }
                return { ...f, start_date: start, end_date };
              });
            }}
            data-testid="report-start"
          /></div>
        <div><Label className="text-xs">End</Label>
          <Input type="date" value={reportForm.end_date} onChange={e => setReportForm(f => ({ ...f, end_date: e.target.value }))} /></div>
        <Button onClick={runReport} className="bg-violet-600 hover:bg-violet-700" data-testid="run-report">Run</Button>
        <label className="flex items-center gap-2 text-xs text-gray-600 cursor-pointer ml-1" title="Show a Weekly Hours column with a 48h (40h for managers) target check">
          <Switch checked={weeklyCol} onCheckedChange={setWeeklyCol} data-testid="weekly-col-toggle" />
          Weekly Hours
        </label>
        {report && report.rows.length > 0 && (
          <>
            <Button onClick={() => exportReport('csv')} variant="outline" data-testid="export-csv">
              <Download className="h-4 w-4 mr-1" /> CSV
            </Button>
            <Button onClick={() => exportReport('xlsx')} variant="outline" data-testid="export-xlsx">
              <Download className="h-4 w-4 mr-1" /> Excel (.xlsx)
            </Button>
            <Button onClick={() => exportReport('xls')} variant="outline" data-testid="export-xls">
              <Download className="h-4 w-4 mr-1" /> Excel (.xls)
            </Button>
            <Button onClick={() => exportReport('pdf')} variant="outline" data-testid="export-pdf">
              <Download className="h-4 w-4 mr-1" /> PDF
            </Button>
            {totalSelected > 0 && (
              <Button
                onClick={bulkDeleteSelected}
                variant="outline"
                disabled={bulkBusy}
                className="text-rose-600 border-rose-300 hover:bg-rose-50"
                data-testid="report-bulk-delete"
              >
                <Trash2 className="h-4 w-4 mr-1" />
                Delete selected ({totalSelected})
              </Button>
            )}
          </>
        )}
      </CardContent></Card>

      {report && (
        <Card><CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-xs uppercase text-gray-600">
              <tr>
                <th className="text-center p-3 w-8">
                  <input
                    type="checkbox"
                    checked={report.rows.length > 0 && report.rows.every(r => selectedSummary[r.employee_id])}
                    onChange={(e) => {
                      const next = {};
                      if (e.target.checked) report.rows.forEach(r => { next[r.employee_id] = true; });
                      setSelectedSummary(next);
                    }}
                    data-testid="report-select-all"
                    title="Select all employees"
                  />
                </th>
                <th className="text-left p-3 w-8"></th>
                <th className="text-left p-3">Employee</th>
                <th className="text-left p-3">Department</th>
                <th className="text-right p-3">Sessions</th>
                <th className="text-right p-3">Total Time</th>
                {weeklyCol && <th className="text-left p-3" data-testid="weekly-col-header">Weekly Hours</th>}
                <th className="text-left p-3">Breakdown ({report.scope})</th>
                <th className="text-right p-3 w-8"></th>
              </tr>
            </thead>
            <tbody>
              {report.rows.length === 0 ? <tr><td colSpan={weeklyCol ? 9 : 8} className="p-6 text-center text-gray-500">No data for the selected range</td></tr> :
                report.rows.map(r => {
                  const isOpen = !!expandedReportRows[r.employee_id];
                  return (
                    <React.Fragment key={r.employee_id}>
                      <tr
                        className={`border-t hover:bg-violet-50/50 ${isOpen ? 'bg-violet-50/50' : ''}`}
                        data-testid={`report-row-${r.employee_id}`}
                      >
                        <td className="p-3 text-center" onClick={(ev) => ev.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={!!selectedSummary[r.employee_id]}
                            onChange={(ev) => setSelectedSummary(p => ({ ...p, [r.employee_id]: ev.target.checked }))}
                            data-testid={`report-summary-cb-${r.employee_code}`}
                          />
                        </td>
                        <td className="p-3 text-violet-600 cursor-pointer" onClick={() => setExpandedReportRows(p => ({ ...p, [r.employee_id]: !isOpen }))}>
                          {r.sessions_count > 0 ? (isOpen ? '▾' : '▸') : ''}
                        </td>
                        <td className="p-3 cursor-pointer" onClick={() => setExpandedReportRows(p => ({ ...p, [r.employee_id]: !isOpen }))}>
                          {r.employee_name} <span className="text-xs text-gray-400">· {r.employee_code}</span>
                        </td>
                        <td className="p-3 text-xs">{r.department}</td>
                        <td className="p-3 text-right">{r.sessions_count}{r.open_session && <span className="ml-1 text-amber-500" title="Has open session">⚠</span>}</td>
                        <td className="p-3 text-right font-mono font-bold text-violet-700">{fmtHM(r.total_hours)}</td>
                        {weeklyCol && (
                          <td className="p-3" data-testid={`weekly-cell-${r.employee_code}`}>
                            {(!r.weekly || r.weekly.length === 0) ? <span className="text-xs text-gray-400">—</span> : (
                              <div className="flex flex-wrap gap-1">
                                {r.weekly.map((w) => {
                                  const cls = w.status === 'low'
                                    ? 'bg-orange-100 text-orange-700 border border-orange-300'
                                    : w.status === 'na'
                                      ? 'bg-gray-100 text-gray-500 border border-gray-200'
                                      : 'bg-emerald-100 text-emerald-700 border border-emerald-300';
                                  const tip = `Week ${w.label}: ${fmtHM(w.hours)} worked · target ${fmtHM(w.adjusted_target)}/${fmtHM(w.threshold)}`
                                    + (w.excused_days ? ` · ${w.excused_days} excused day(s)` : '')
                                    + (w.status === 'low' ? ' · BELOW target' : w.status === 'na' ? ' · fully on leave' : ' · meets target');
                                  return (
                                    <span key={w.week_start} className={`px-1.5 py-0.5 rounded text-[11px] font-mono ${cls}`} title={tip}>
                                      {w.label}: {fmtHM(w.hours)}
                                    </span>
                                  );
                                })}
                              </div>
                            )}
                          </td>
                        )}
                        <td className="p-3 text-xs">
                          {r.buckets.length === 0 ? '—' :
                            <div className="space-y-1">
                              {/* 7-day spark-bar: shows up to last 7 buckets as relative-height bars */}
                              {r.buckets.length >= 2 && (() => {
                                const recent = r.buckets.slice(-7);
                                const maxH = Math.max(...recent.map(b => b.hours || 0), 0.001);
                                return (
                                  <div className="flex items-end gap-0.5 h-6" title="Hours per bucket (last 7)">
                                    {recent.map(b => {
                                      const pct = Math.max(2, Math.round((b.hours || 0) / maxH * 100));
                                      const tall = (b.hours || 0) >= 8;
                                      return (
                                        <div
                                          key={b.label}
                                          className={`w-2 rounded-sm ${tall ? 'bg-violet-500' : 'bg-violet-300'}`}
                                          style={{ height: `${pct}%`, minHeight: '2px' }}
                                          title={`${b.label}: ${fmtHM(b.hours)}`}
                                        />
                                      );
                                    })}
                                  </div>
                                );
                              })()}
                              <div className="flex flex-wrap gap-1">
                                {r.buckets.map(b => <span key={b.label} className="px-1.5 py-0.5 rounded bg-violet-50 text-violet-700 font-mono">{b.label}: {fmtHM(b.hours)}</span>)}
                              </div>
                            </div>}
                        </td>
                        <td className="p-3 text-right" onClick={(ev) => ev.stopPropagation()}>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-rose-600 hover:bg-rose-50"
                            onClick={() => deleteEmployeeRow(r)}
                            title="Delete all punches for this employee in the selected range"
                            data-testid={`report-row-delete-${r.employee_code}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </td>
                      </tr>
                      {isOpen && r.sessions && r.sessions.length > 0 && (
                        <tr className="border-t bg-gray-50/70">
                          <td></td>
                          <td colSpan={weeklyCol ? 8 : 7} className="p-0">
                            <div className="px-3 py-3">
                              <p className="text-[11px] uppercase text-gray-500 font-semibold mb-2">
                                Sessions Detail — {r.employee_name}
                              </p>
                              <table className="w-full text-xs">
                                <thead className="text-[10px] uppercase text-gray-500">
                                  <tr>
                                    <th className="text-center py-1 pr-2 w-6">
                                      <input
                                        type="checkbox"
                                        checked={r.sessions.length > 0 && r.sessions.every((_s, i) => selectedSessions[`${r.employee_id}|${i}`])}
                                        onChange={(ev) => {
                                          setSelectedSessions(p => {
                                            const next = { ...p };
                                            r.sessions.forEach((_s, i) => {
                                              const k = `${r.employee_id}|${i}`;
                                              if (ev.target.checked) next[k] = true; else delete next[k];
                                            });
                                            return next;
                                          });
                                        }}
                                        data-testid={`sessions-select-all-${r.employee_code}`}
                                      />
                                    </th>
                                    <th className="text-left py-1 pr-2">#</th>
                                    <th className="text-left py-1 pr-2">Clocked In</th>
                                    <th className="text-left py-1 pr-2">Clocked Out</th>
                                    <th className="text-right py-1 pr-2">Duration</th>
                                    <th className="text-left py-1 pr-2">Station</th>
                                    <th className="text-left py-1">Notes</th>
                                    <th className="text-right py-1 w-6"></th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {r.sessions.map((s, i) => {
                                    const sessionKey = `${r.employee_id}|${i}`;
                                    return (
                                    <tr key={i} className="border-t border-gray-200">
                                      <td className="py-1.5 pr-2 text-center">
                                        <input
                                          type="checkbox"
                                          checked={!!selectedSessions[sessionKey]}
                                          onChange={(ev) => setSelectedSessions(p => ({ ...p, [sessionKey]: ev.target.checked }))}
                                          data-testid={`session-cb-${r.employee_code}-${i}`}
                                        />
                                      </td>
                                      <td className="py-1.5 pr-2 text-gray-500">{i + 1}</td>
                                      <td className="py-1.5 pr-2 font-mono text-emerald-700">{fmtDateTime(s.in)}</td>
                                      <td className="py-1.5 pr-2 font-mono text-sky-700">
                                        {s.out ? fmtDateTime(s.out) : (
                                          <span className="text-red-600 font-sans font-medium" title="No clock-out was recorded for this clock-in">⚠ Missed clock-out</span>
                                        )}
                                      </td>
                                      <td className="py-1.5 pr-2 text-right font-mono">{s.out ? fmtHM(s.hours) : '—'}</td>
                                      <td className="py-1.5 pr-2 text-gray-600">{s.branch_name || '—'}</td>
                                      <td className="py-1.5">
                                        <input
                                          type="text"
                                          defaultValue={s.note || ''}
                                          placeholder="Late / Early leave / No-show…"
                                          onBlur={(e) => saveDayNote(r.employee_id, s.date, e.target.value)}
                                          onKeyDown={(e) => { if (e.key === 'Enter') e.target.blur(); }}
                                          className="w-full px-1.5 py-0.5 text-xs border border-gray-200 rounded focus:border-violet-400 focus:outline-none bg-white"
                                          data-testid={`day-note-${r.employee_id}-${s.date}`}
                                        />
                                      </td>
                                      <td className="py-1.5 text-right">
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-6 w-6 p-0 text-rose-600 hover:bg-rose-50"
                                          onClick={() => deleteSession(s)}
                                          title="Delete this session"
                                          data-testid={`session-delete-${r.employee_code}-${i}`}
                                        >
                                          <Trash2 className="h-3 w-3" />
                                        </Button>
                                      </td>
                                    </tr>
                                    );
                                  })}
                                  {r.open_session && (
                                    <tr className="border-t border-gray-200 text-amber-600 italic">
                                      <td></td>
                                      <td className="py-1.5 pr-2">{r.sessions.length + 1}</td>
                                      <td className="py-1.5 pr-2 font-mono">— still clocked in —</td>
                                      <td className="py-1.5 pr-2"></td>
                                      <td className="py-1.5 pr-2 text-right">in progress</td>
                                      <td className="py-1.5"></td>
                                      <td className="py-1.5"></td>
                                      <td className="py-1.5 text-right">
                                        {r.open_event_id && (
                                          <Button
                                            size="sm"
                                            variant="outline"
                                            className="h-6 px-1.5 text-[10px] text-sky-700 border-sky-300"
                                            onClick={() => onClockOut && onClockOut({
                                              event_id: r.open_event_id,
                                              employee_id: r.employee_id,
                                              employee_name: r.employee_name,
                                              employee_code: r.employee_code,
                                              in_at: null,
                                            })}
                                            data-testid={`report-open-clockout-${r.employee_code}`}
                                          >
                                            <LogOut className="h-3 w-3 mr-0.5" /> Clock out
                                          </Button>
                                        )}
                                      </td>
                                    </tr>
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
            </tbody>
          </table>
        </CardContent></Card>
      )}
    </div>
  );
};

export default ReportsTab;
